import React, { useEffect, useState } from 'react';
import './TravelOffersView.css';

const ENV_BASE_URL = process.env.REACT_APP_API_BASE_URL;
let BASE_URL =
  ENV_BASE_URL ||
  (process.env.NODE_ENV === "production"
    ? window.location.origin
    : "http://localhost:8012");

const TRAVEL_OFFER = BASE_URL + '/v2/travelagent/travel_offers';
const TRAVEL_OFFER_BY_URLS = BASE_URL + '/v2/travelagent/travel_offers/by_urls';
const TRAVEL_OFFER_REFRESH = BASE_URL + '/v2/travelagent/travel_offers/refresh';

const TravelOffersView = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [cardFilter, setCardFilter] = useState('');
  const [merchantFilter, setMerchantFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [benefitFilter, setBenefitFilter] = useState('all');
  const [manualUrls, setManualUrls] = useState('');

  const fetchOffers = async () => {
    setLoading(true);
    setError(null);
    try {
      
      // NOTE: frontend.env sets REACT_APP_BACKEND_URL, apiService uses it too.
    
      const resp = await fetch(TRAVEL_OFFER);
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setOffers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to fetch travel offers:', e);
      setError(e.message || 'Failed to fetch offers');
    } finally {
      setLoading(false);
    }
  };

  const fetchOffersFromUrls = async () => {
    const urls = manualUrls
      .split('\n')
      .map((u) => u.trim())
      .filter((u) => u.length > 0);
    if (urls.length === 0) {
      setError('Please enter at least one DBS promotion URL below.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(TRAVEL_OFFER_BY_URLS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ urls }),
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      // Backend already appended new offers into cache; just refetch full list
      await fetchOffers();
      // Clear input after successful submission
      setManualUrls('');
    } catch (e) {
      console.error('Failed to fetch travel offers from URLs:', e);
      setError(e.message || 'Failed to fetch offers from URLs');
    } finally {
      setLoading(false);
    }
  };

  const handleForceRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const resp = await fetch(TRAVEL_OFFER_REFRESH, {
        method: 'POST',
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      // After backend cache refresh, re-fetch offers list
      await fetchOffers();
    } catch (e) {
      console.error('Failed to refresh travel offers:', e);
      setError(e.message || 'Failed to refresh offers');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const matchesFilter = (offer) => {
    const card = cardFilter.trim().toLowerCase();
    const merchant = merchantFilter.trim().toLowerCase();

    if (categoryFilter !== 'all' && offer.merchant_category !== categoryFilter) {
      return false;
    }
    if (benefitFilter !== 'all' && offer.benefit_type !== benefitFilter) {
      return false;
    }

    if (card) {
      const cards = (offer.eligible_cards || []).join(' ').toLowerCase();
      const text = (offer.raw_text || '').toLowerCase();
      if (!cards.includes(card) && !text.includes(card)) {
        return false;
      }
    }

    if (merchant) {
      const name = (offer.merchant_name || '').toLowerCase();
      const text = (offer.raw_text || '').toLowerCase();
      if (!name.includes(merchant) && !text.includes(merchant)) {
        return false;
      }
    }

    return true;
  };

  const filteredOffers = offers.filter(matchesFilter);

  return (
    <div className="travel-offers-page">
      <h1 className="travel-offers-title">DBS Travel Card Offers</h1>
      <p className="travel-offers-subtitle">
        Browse card-related travel privileges from DBS/POSB, and filter by card, merchant, or benefit type.
      </p>

      <div className="travel-offers-filters">
        <input
          type="text"
          placeholder="Filter by card name (e.g. DBS Altitude)"
          value={cardFilter}
          onChange={(e) => setCardFilter(e.target.value)}
        />
        <input
          type="text"
          placeholder="Filter by merchant (e.g. Singapore Airlines, Hilton)"
          value={merchantFilter}
          onChange={(e) => setMerchantFilter(e.target.value)}
        />
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="all">All categories</option>
          <option value="airline">Airlines</option>
          <option value="hotel">Hotels</option>
          <option value="other">Others</option>
        </select>
        <select value={benefitFilter} onChange={(e) => setBenefitFilter(e.target.value)}>
          <option value="all">All benefit types</option>
          <option value="cashback">Cashback</option>
          <option value="discount">Discount</option>
          <option value="other">Other</option>
        </select>
        <button
          className="travel-offers-refresh"
          onClick={handleForceRefresh}
          disabled={loading || refreshing}
        >
          {refreshing ? 'Refreshing from DBS…' : 'Refresh from DBS'}
        </button>
      </div>

      <div className="travel-offers-manual">
        <textarea
          className="manual-textarea"
          value={manualUrls}
          onChange={(e) => setManualUrls(e.target.value)}
          placeholder="One DBS promotion URL per line..."
        />
        <div className="manual-actions">
          <button
            className="travel-offers-refresh"
            onClick={fetchOffersFromUrls}
            disabled={loading}
          >
            {loading ? 'Fetching…' : 'Fetch offers from these URLs'}
          </button>
        </div>
      </div>

      {loading && <div className="travel-offers-status">Loading offers...</div>}
      {error && <div className="travel-offers-status error">Error: {error}</div>}

      {!loading && !error && filteredOffers.length === 0 && (
        <div className="travel-offers-status">No offers match current filters.</div>
      )}

      <div className="travel-offers-grid-wrapper">
        <div className="travel-offers-grid">
          {filteredOffers.map((offer, idx) => (
            <div className="travel-offer-card" key={`${offer.source_url}-${idx}`}>
              <div className="travel-offer-header">
                <span className={`travel-offer-category badge badge-${offer.merchant_category}`}>
                  {offer.merchant_category}
                </span>
                <span className={`travel-offer-benefit badge badge-${offer.benefit_type}`}>
                  {offer.benefit_type}
                </span>
              </div>
              <h2 className="travel-offer-title">{offer.title || offer.merchant_name}</h2>
              <p className="travel-offer-merchant">{offer.merchant_name}</p>

              {offer.eligible_cards && offer.eligible_cards.length > 0 && (
                <div className="travel-offer-section">
                  <div className="label">Eligible cards</div>
                  <ul className="pill-list">
                    {offer.eligible_cards.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}

              {offer.usage_instructions && (
                <div className="travel-offer-section">
                  <div className="label">How to use</div>
                  <p className="mono small">{offer.usage_instructions}</p>
                </div>
              )}

              {offer.validity_period && (
                <div className="travel-offer-section">
                  <div className="label">Validity</div>
                  <div className="small">{offer.validity_period}</div>
                </div>
              )}

              <div className="travel-offer-footer">
                <a href={offer.source_url} target="_blank" rel="noreferrer">
                  View on DBS
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TravelOffersView;

